import React, { useState, useEffect } from 'react';
import { adsAPI } from '../services/api';
import { FaSpinner, FaExternalLinkAlt } from 'react-icons/fa';

const Ads = ({ location = 'encounters_page', limit = 2 }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAds = async () => {
      try {
        const response = await adsAPI.getAdsByLocation(location);
        const data = response.data || response;

        if (data && data.success && data.ads) {
          setAds(data.ads.slice(0, limit)); // Limit ads
        } else {
          console.warn('No ads available for location:', location);
          setAds([]);
        }
      } catch (error) {
        console.warn('Failed to load ads:', error.message);
        setAds([]);
      } finally {
        setLoading(false);
      }
    };

    if (location) {
      loadAds();
    }
  }, [location, limit]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center justify-center h-24">
          <FaSpinner className="animate-spin h-6 w-6 text-pink-500" />
        </div>
      </div>
    );
  }

  if (!ads || ads.length === 0) {
    return null;
  }

  const renderAd = (ad, index) => {
    const colors = [
      'from-pink-50 to-purple-50',
      'from-blue-50 to-indigo-50',
      'from-green-50 to-teal-50',
      'from-yellow-50 to-orange-50'
    ];

    const colorClass = colors[index % colors.length];

    return (
      <div
        key={ad.id}
        className={`bg-gradient-to-r ${colorClass} rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer`}
        onClick={() => {
          if (ad.link) {
            window.open(ad.link, '_blank');
          }
        }}
      >
        <div className="flex items-start space-x-3">
          {/* Media */}
          {ad.media && (
            <div className="flex-shrink-0">
              {ad.media_type === 'video' ? (
                <video
                  src={ad.media}
                  className="w-16 h-16 object-cover rounded"
                  muted
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => e.target.pause()}
                />
              ) : ad.media_type === 'gif' ? (
                <img
                  src={ad.media}
                  alt={ad.title}
                  className="w-16 h-16 object-cover rounded"
                />
              ) : (
                <img
                  src={ad.media}
                  alt={ad.title}
                  className="w-16 h-16 object-cover rounded"
                />
              )}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 truncate">
              {ad.title}
            </h4>
            {ad.description && (
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                {ad.description}
              </p>
            )}
            {ad.link && (
              <div className="flex items-center mt-2 text-xs text-pink-600">
                <FaExternalLinkAlt className="h-3 w-3 mr-1" />
                <span>Learn More</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
      <div className="grid grid-cols-1 gap-3">
        {ads.map((ad, index) => renderAd(ad, index))}
      </div>

      {ads.length > 0 && (
        <div className="text-center mt-3">
          <span className="text-xs text-gray-400">Sponsored</span>
        </div>
      )}
    </div>
  );
};

export default Ads;
